import { useEffect, useMemo, useState } from 'react'
import type { NavigateFunction } from 'react-router'

import type { ToastLike } from '@mangovoice/mango-ui'

import {
  useDeleteDevice,
  useGetDevices,
  useMassEditDevices,
  useRebuildAndRebootDevices,
  useRebuildDevices,
  useReloadConfiguration,
  useReloadConfigurationAndReboot,
  useSetDesiredFirmwareVersionAndReload,
} from '../hooks'
import {
  type DeviceMassEditItem,
  type DevicesListFilters as DevicesListFiltersType,
  type DeviceSortColumn,
  type DeviceSortState,
  type ProvisioningDeviceListItem,
  type SortDirection,
} from '../types'
import {
  formatApiError,
  normalizeSearchTerm,
  TOAST_DURATION_SUCCESS,
} from '../utils'

const DEVICES_ALL_LIMIT = 10000

export function useDevicesListState({
  pbxId,
  navigate,
  toast,
  getEditRoute,
}: {
  pbxId: number
  navigate: NavigateFunction
  toast: ToastLike
  getEditRoute: (deviceId: number) => string
}) {
  const hasValidPbxId = pbxId > 0

  const showSuccessToast = (title: string, description?: string) => {
    toast.create({
      title,
      description,
      type: 'success',
      duration: TOAST_DURATION_SUCCESS,
    })
  }

  const showErrorToast = (title: string, error: unknown) => {
    toast.create({
      title,
      description: formatApiError(error),
      type: 'error',
    })
  }

  // ---- Pagination, search, filters, sorting ----

  const [pageSize, setPageSize] = useState<number | 'All'>(25)
  const [currentPage, setCurrentPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const [filters, setFilters] = useState<Partial<DevicesListFiltersType>>({})
  const [filtersVisible, setFiltersVisible] = useState(false)

  const activeFilterCount = useMemo(() => {
    let count = 0
    if (filters.manufacturer_id !== undefined) count++
    if (filters.device_model !== undefined) count++
    if (filters.firmware_version) count++
    if (filters.warranty) count++
    if (filters.transport) count++
    if (filters.jitter) count++
    if (filters.sidecar_enabled !== undefined) count++
    if (filters.provisioning) count++
    return count
  }, [filters])

  const [sortState, setSortState] = useState<DeviceSortState>({
    column: null,
    direction: 'asc',
  })

  const offset = useMemo(() => {
    if (pageSize === 'All') return 0
    return (currentPage - 1) * pageSize
  }, [currentPage, pageSize])

  const limit = useMemo(() => {
    if (pageSize === 'All') return DEVICES_ALL_LIMIT
    return pageSize
  }, [pageSize])

  const ordering = useMemo(() => {
    if (!sortState.column) return undefined
    const prefix = sortState.direction === 'desc' ? '-' : ''
    const apiColumn =
      sortState.column === 'device_model'
        ? 'device_model__name'
        : sortState.column === 'assigned_user'
          ? 'assigned_user__last_name'
          : sortState.column === 'primary_extension'
            ? 'primary_extension_number'
            : sortState.column
    return `${prefix}${apiColumn}`
  }, [sortState])

  // ---- Query ----

  const {
    data: devicesData,
    isLoading,
    error,
  } = useGetDevices(
    pbxId,
    {
      limit,
      offset,
      search: searchTerm ? normalizeSearchTerm(searchTerm) : undefined,
      ordering,
      ...filters,
    },
    { enabled: hasValidPbxId },
  )

  const devices = devicesData?.results ?? []
  const totalCount = devicesData?.count ?? 0

  // ---- Sorting handler ----

  const handleSort = (column: DeviceSortColumn, direction: SortDirection) => {
    setSortState({ column, direction })
    setCurrentPage(1)
  }

  // ---- Selection & dialog state ----

  const [deviceToDelete, setDeviceToDelete] =
    useState<ProvisioningDeviceListItem | null>(null)
  const [deviceToClone, setDeviceToClone] =
    useState<ProvisioningDeviceListItem | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [massEditOpen, setMassEditOpen] = useState(false)
  const [setFirmwareDialogOpen, setSetFirmwareDialogOpen] = useState(false)

  // ---- Mutations ----

  const deleteMutation = useDeleteDevice(pbxId)
  const rebuildMutation = useRebuildDevices(pbxId)
  const rebuildRebootMutation = useRebuildAndRebootDevices(pbxId)
  const massEditMutation = useMassEditDevices(pbxId)
  const reloadConfigMutation = useReloadConfiguration(pbxId)
  const reloadConfigRebootMutation = useReloadConfigurationAndReboot(pbxId)
  const setFirmwareMutation = useSetDesiredFirmwareVersionAndReload(pbxId)

  // ---- Handlers ----

  const handleDeleteConfirm = () => {
    if (!deviceToDelete) return
    const deletedId = deviceToDelete.id
    deleteMutation.mutate(deviceToDelete.id, {
      onSuccess: () => {
        setSelectedIds((prev) => {
          if (!prev.has(deletedId)) return prev
          const next = new Set(prev)
          next.delete(deletedId)
          return next
        })
        setDeviceToDelete(null)
        showSuccessToast('Device deleted')
      },
      onError: (err) => showErrorToast('Delete failed', err),
    })
  }

  const handleRebuildAll = (options?: { onSettled?: () => void }) => {
    rebuildMutation.mutate(undefined, {
      onSuccess: () =>
        showSuccessToast('Rebuild started', 'All devices are being rebuilt.'),
      onError: (err) => showErrorToast('Rebuild failed', err),
      onSettled: options?.onSettled,
    })
  }

  const handleRebuildAndReboot = (options?: { onSettled?: () => void }) => {
    rebuildRebootMutation.mutate(undefined, {
      onSuccess: () =>
        showSuccessToast(
          'Rebuild and reboot started',
          'All devices are being rebuilt and rebooted.',
        ),
      onError: (err) => showErrorToast('Rebuild and reboot failed', err),
      onSettled: options?.onSettled,
    })
  }

  const handleMassEditConfirm = (
    payload: DeviceMassEditItem[],
    reboot: boolean,
  ) => {
    massEditMutation.mutate(
      { payload, reboot },
      {
        onSuccess: (result) => {
          setMassEditOpen(false)
          setSelectedIds(new Set())

          if (result?.warning) {
            // Show warning toast if reload/reboot failed
            toast.create({
              title: 'Devices updated with warning',
              description: result.warning,
              type: 'warning',
            })
          } else {
            // Show success toast if everything succeeded
            showSuccessToast(
              reboot ? 'Devices updated and rebooting' : 'Devices updated',
              reboot
                ? `${payload.length} device(s) updated; reboot requested where supported.`
                : `${payload.length} device(s) updated successfully.`,
            )
          }
        },
        onError: (err) => showErrorToast('Mass edit failed', err),
      },
    )
  }

  const handleCloneSuccess = (newDeviceId: number) => {
    setDeviceToClone(null)
    showSuccessToast('Device cloned')
    navigate(getEditRoute(newDeviceId))
  }

  const handleCloneError = (err: unknown) => {
    showErrorToast('Clone failed', err)
  }

  const selectedIdsArray = useMemo(() => Array.from(selectedIds), [selectedIds])

  useEffect(() => {
    if (selectedIds.size === 0) return
    const visibleIds = new Set(devices.map((d) => d.id))
    setSelectedIds((prev) => {
      let changed = false
      const next = new Set<number>()
      for (const id of prev) {
        if (visibleIds.has(id)) next.add(id)
        else changed = true
      }
      return changed ? next : prev
    })
  }, [devices, selectedIds.size])

  const handleReloadConfiguration = () => {
    if (selectedIdsArray.length === 0) return
    reloadConfigMutation.mutate(selectedIdsArray, {
      onSuccess: () => {
        setSelectedIds(new Set())
        showSuccessToast(
          'Reload configuration started',
          `${selectedIdsArray.length} device(s) are being reloaded.`,
        )
      },
      onError: (err) => showErrorToast('Reload configuration failed', err),
    })
  }

  const handleReloadConfigurationAndReboot = () => {
    if (selectedIdsArray.length === 0) return
    reloadConfigRebootMutation.mutate(selectedIdsArray, {
      onSuccess: () => {
        setSelectedIds(new Set())
        showSuccessToast(
          'Reload and reboot started',
          `${selectedIdsArray.length} device(s) are being reloaded and rebooted.`,
        )
      },
      onError: (err) => showErrorToast('Reload and reboot failed', err),
    })
  }

  const handleSetDesiredFirmwareConfirm = (desiredFirmwareVersion: string) => {
    if (selectedIdsArray.length === 0) return
    setFirmwareMutation.mutate(
      {
        desired_firmware_version: desiredFirmwareVersion,
        device_ids: selectedIdsArray,
      },
      {
        onSuccess: () => {
          setSetFirmwareDialogOpen(false)
          setSelectedIds(new Set())
          showSuccessToast(
            'Desired firmware set',
            `${selectedIdsArray.length} device(s) will be updated and reloaded.`,
          )
        },
        onError: (err) => showErrorToast('Set desired firmware failed', err),
      },
    )
  }

  const isAnyMutationPending =
    reloadConfigMutation.isPending ||
    reloadConfigRebootMutation.isPending ||
    setFirmwareMutation.isPending ||
    rebuildMutation.isPending ||
    rebuildRebootMutation.isPending

  return {
    // Query
    hasValidPbxId,
    devices,
    totalCount,
    isLoading,
    error,
    // Pagination
    pageSize,
    setPageSize,
    currentPage,
    setCurrentPage,
    // Search & filters
    searchTerm,
    setSearchTerm,
    filters,
    setFilters,
    filtersVisible,
    setFiltersVisible,
    activeFilterCount,
    // Sorting
    sortState,
    handleSort,
    // Selection
    selectedIds,
    setSelectedIds,
    selectedIdsArray,
    // Dialog state
    deviceToDelete,
    setDeviceToDelete,
    deviceToClone,
    setDeviceToClone,
    massEditOpen,
    setMassEditOpen,
    setFirmwareDialogOpen,
    setSetFirmwareDialogOpen,
    // Handlers
    handleDeleteConfirm,
    handleRebuildAll,
    handleRebuildAndReboot,
    handleMassEditConfirm,
    handleCloneSuccess,
    handleCloneError,
    handleReloadConfiguration,
    handleReloadConfigurationAndReboot,
    handleSetDesiredFirmwareConfirm,
    // Mutation state
    isAnyMutationPending,
    isDeleting: deleteMutation.isPending,
    isMassEditing: massEditMutation.isPending,
    isSettingFirmware: setFirmwareMutation.isPending,
    isReloadingConfig: reloadConfigMutation.isPending,
    isReloadingConfigAndRebooting: reloadConfigRebootMutation.isPending,
    isRebuilding: rebuildMutation.isPending,
    isRebuildingAndRebooting: rebuildRebootMutation.isPending,
  }
}
